import React from "react";
import { useTranslation } from "react-i18next";
import { UseCases } from "@planzone/core";
import { Props } from "./types";
import { WorkspaceContext } from "@scenes/workspaceContext";
import { Notifications } from "@scenes/notifications";
import { Safeguard } from "@scenes/safeguard";
import { AssistantShortcut } from "@scenes/assistantShortcut";
import { ProfileNavigation } from "@common/profileNavigation";
import { ResourceProjectAllocation } from "./resourceProjectAllocation";
import { CustomCpamReport } from "./customCpamReport";

import {
  useRetrieveCurrentPlanzone,
  useAuthentication,
} from "@planzone/view-models";
import { ResourcesReport } from "./resourceReport";
import { WbsReport } from "./wbsReport";
import { Loader } from "./loader";
import { TimeSheetReport } from "./timesheetReport";
import { TimeSheetByTaskReport } from "./timesheetByTaskReport";
import { TaskBudgetReport } from "./taskBudgetReport";
import { ResourceAllocatedTimeReport } from "./resourceAllocatedTimeReport";
import { BiExportReport } from "./biExportReport";
import styles from "./styles.scss";

function ExportsPage(props: Props) {
  const { t } = useTranslation();
  const { account } = useAuthentication();
  const { planzone } = useRetrieveCurrentPlanzone();

  return (
    <WorkspaceContext type="planzone">
      <div className={styles.exportReportsPage}>
        <div className={styles.header}>
          <h1 className={styles.title}>{t("exportReportsPage.title")}</h1>
          <div className={styles.profileNavigation}>
            <AssistantShortcut />
            <Notifications />
            <ProfileNavigation />
          </div>
        </div>
        <div className={styles.band}>
          <h3 className={styles.subTitle}>{t("exportReportsPage.subTitle")}</h3>
          <p className={styles.text}>{t("exportReportsPage.description")}</p>
        </div>
        <div className={styles.main}>
          <div className={styles.col}>
            <h1 className={styles.subTitle}>
              {t("exportReportsPage.reportType.title")}
            </h1>
            <p className={styles.description}>
              {t("exportReportsPage.reportType.subTitle")}
            </p>
          </div>
          <div className={styles.list}>
            <ResourcesReport
              fileName={`${t("exportReportsPage.resourceReport.name")} ${
                planzone.name
              }`}
              author={account}
              planzone={planzone}
            />
          </div>
          <div className={styles.list}>
            <WbsReport
              fileName={`${t("exportReportsPage.wbsZonesReport.name")} ${
                planzone.name
              }`}
              author={account}
              planzone={planzone}
            />
          </div>
          <div className={styles.list}>
            <TimeSheetReport
              fileName={`${t("exportReportsPage.timeSheetReport.name")} ${
                planzone.name
              }`}
              author={account}
              planzone={planzone}
            />
          </div>
          <div className={styles.list}>
            <TimeSheetByTaskReport
              fileName={
                t("exportReportsPage.timeSheetByTaskReport.name") +
                planzone.name
              }
              author={account}
              planzone={planzone}
            />
          </div>
          {planzone.uuid === "c1b41607-7ed7-4f57-8167-38bce99526df" && (
            <div className={styles.list}>
              <TaskBudgetReport
                fileName={
                  t("exportReportsPage.taskBudgetReport.name") + planzone.name
                }
                author={account}
                planzone={planzone}
              />
            </div>
          )}
          {planzone.uuid === "57ec7e80-dcbb-49af-b11f-665fa6e545c1" && (
            <div className={styles.list}>
              <ResourceAllocatedTimeReport
                fileName={`${t(
                  "exportReportsPage.resourceAllocatedTimeReport.name",
                )} ${planzone.name}`}
                author={account}
                planzone={planzone}
              />
            </div>
          )}
          {planzone.uuid === "57ec7e80-dcbb-49af-b11f-665fa6e545c1" && (
            <Safeguard useCases={[UseCases.requestExportBiRun]}>
              {({ canRequestExportBiRun }) =>
                canRequestExportBiRun && (
                  <div className={styles.list}>
                    <BiExportReport planzone={planzone} />
                  </div>
                )
              }
            </Safeguard>
          )}
          {(planzone.uuid === "028bb8bc-a8d1-47d7-8006-7e128be3eb87" ||
            planzone.uuid === "2a1573e9-d5e1-40bd-985b-5459c05c28bc") && (
            <div className={styles.list}>
              <ResourceProjectAllocation
                fileName={
                  t("exportReportsPage.resourceProjectAllocationReport.name") +
                  planzone.name
                }
                author={account}
                planzone={planzone}
              />
            </div>
          )}
          {window.location.hostname === "web.preprod.planzone7.com" &&
            (planzone.uuid === "d980bc6f-06d6-470d-bae7-0c587d774888" ||
              planzone.uuid === "47a216e3-62ff-40ae-ae1f-398ff89c5da4") && (
              <div className={styles.list}>
                <CustomCpamReport
                  fileName={
                    t("exportReportsPage.customCpamReport.name") + planzone.name
                  }
                  author={account}
                  planzone={planzone}
                />
              </div>
            )}
        </div>
      </div>
    </WorkspaceContext>
  );
}

export { ExportsPage };
